import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, switchMap, catchError, defer, EMPTY, debounceTime, finalize } from 'rxjs';
import { SendService } from '../services/send.service';
import { AddTokenService } from '../services/add-token.service';
import { PinEncryptionService } from '../services/pin-encryption.service';
import { TransactionStatusService } from '../services/transaction-status.service';
import { WalletDialogFlow } from './wallet-dialog-flow';
import { PinAttemptStore } from './pin-attempt.store';
import { GozemApiError, isAuthStageError } from '@shared/utils/http-error.util';
import { toE164 } from '@shared/utils/phone.util';
import type { AddTokenFormValue } from '../models/add-token.model';
import type {
  SendDialogStep,
  SendAuthTxnResult,
  SendSuccessState,
  TopupFee,
} from '../models/send.model';
import type { PostTxStatus, TopupCategory, TopupMethod, TopupToken } from '../models/topup.model';

/**
 * SendStore
 *
 * Scoped store for the "Envoyer de l'argent" dialog.
 * Provided at SendModal level via `providers: [SendStore]`.
 *
 * Flow:
 *   Step 1 — Amount
 *   Step 2 — Method + Token (PSP: fetch saved tokens then auth_txn after selection;
 *             Gozem Money: auth_txn fires immediately on method selection)
 *   Step 3 — Confirm (summary + PIN entry)
 *
 * Two submit paths (determined by selected method's is_gozem_money_processor flag):
 *   Gozem path → init_transfer  (payee_gm_account_number from auth_txn result)
 *   PSP path   → init_psp_transfer (token_id + withdrawal_id from auth_txn)
 *
 * No separate verify_pin step: PIN is RSA-OAEP encrypted and sent directly
 * as authentication_data in the init request.
 *
 * Architecture: Component → Store → Service → Api → Backend
 */
@Injectable()
export class SendStore {
  private readonly sendService = inject(SendService);
  private readonly addTokenService = inject(AddTokenService);
  private readonly pinEncryption = inject(PinEncryptionService);
  private readonly transactionStatus = inject(TransactionStatusService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly flow = new WalletDialogFlow(this.destroyRef);
  /** Dialog-scoped PIN attempt / lockout state (backend-owned counter). */
  private readonly pinStore = inject(PinAttemptStore);

  // ── Mutable private state ─────────────────────────────────────────────────

  private readonly _amount = signal<number | null>(null);
  private readonly _dialogStep = signal<SendDialogStep>('form');

  // Methods
  private readonly _methods = signal<TopupMethod[]>([]);
  private readonly _methodsLoading = signal(false);
  private readonly _methodsError = signal<string | null>(null);

  // Selected method
  private readonly _selectedMethod = signal<TopupMethod | null>(null);

  // Tokens (saved accounts for PSP methods)
  private readonly _tokens = signal<TopupToken[]>([]);
  private readonly _tokensLoading = signal(false);
  private readonly _tokensError = signal<string | null>(null);
  private readonly _selectedToken = signal<TopupToken | null>(null);

  // Add token
  private readonly _addTokenLoading = signal(false);
  private readonly _addTokenError = signal<string | null>(null);

  // Auth transaction
  private readonly _authTxnResult = signal<SendAuthTxnResult | null>(null);
  private readonly _authTxnLoading = signal(false);
  private readonly _authTxnError = signal<string | null>(null);

  // Category + note
  private readonly _categoryId = signal<string>('');
  private readonly _message = signal<string>('');

  // Submit
  private readonly _submitting = signal(false);
  private readonly _error = signal<string | null>(null);
  // Terminal-failure event. Carries a monotonic `seq` so that two consecutive
  // failures — even with an identical message — always notify subscribers.
  // Routing off `_dialogStep` alone is a signal-equality no-op when it is
  // already 'failure', which previously swallowed the error on repeat attempts.
  private readonly _submitFailure = signal<{ message: string; seq: number } | null>(null);
  private submitFailureSeq = 0;
  private readonly _lastSendResult = signal<SendSuccessState | null>(null);
  private readonly _txDetailLoading = signal(false);
  private readonly _txStatus = signal<PostTxStatus | null>(null);

  // ── Public readonly state ─────────────────────────────────────────────────

  readonly amount = this._amount.asReadonly();
  readonly dialogStep = this._dialogStep.asReadonly();

  readonly methods = this._methods.asReadonly();
  readonly methodsLoading = this._methodsLoading.asReadonly();
  readonly methodsError = this._methodsError.asReadonly();

  readonly selectedMethod = this._selectedMethod.asReadonly();

  readonly tokens = this._tokens.asReadonly();
  readonly tokensLoading = this._tokensLoading.asReadonly();
  readonly tokensError = this._tokensError.asReadonly();
  readonly selectedToken = this._selectedToken.asReadonly();
  readonly addTokenLoading = this._addTokenLoading.asReadonly();
  readonly addTokenError = this._addTokenError.asReadonly();

  readonly authTxnResult = this._authTxnResult.asReadonly();
  readonly authTxnLoading = this._authTxnLoading.asReadonly();
  readonly authTxnError = this._authTxnError.asReadonly();

  readonly categoryId = this._categoryId.asReadonly();
  readonly message = this._message.asReadonly();

  readonly submitting = this._submitting.asReadonly();
  readonly error = this._error.asReadonly();
  /** Always-notifying terminal-failure event — drives navigation to the failure screen. */
  readonly submitFailure = this._submitFailure.asReadonly();
  readonly lastSendResult = this._lastSendResult.asReadonly();
  readonly txDetailLoading = this._txDetailLoading.asReadonly();
  readonly txStatus = this._txStatus.asReadonly();

  // PIN attempt / lockout state — owned by the dialog-scoped PinAttemptStore.
  // A wrong PIN is an authentication error, NOT a transaction failure: it keeps
  // the user on the PIN step with an inline message instead of routing to the
  // "Transfert échoué" result screen.
  readonly pinAttempts = this.pinStore.pinAttempts;
  readonly pinError = this.pinStore.pinError;
  readonly pinLocked = this.pinStore.pinLocked;
  readonly pinErrorMessage = this.pinStore.pinErrorMessage;

  // ── Computed ──────────────────────────────────────────────────────────────

  readonly isAmountValid = computed(() => {
    const a = this._amount();
    return a !== null && a > 0;
  });

  /** Categories from the auth_txn result */
  readonly categories = computed<TopupCategory[]>(() => this._authTxnResult()?.categories ?? []);

  /** Currently selected category object */
  readonly selectedCategory = computed(
    () => this.categories().find((c) => c.id === this._categoryId()) ?? null,
  );

  /** Total = amount + fee from auth_txn (or method.fee as immediate fallback) */
  readonly totalWithFees = computed(() => {
    const base = this._amount() ?? 0;
    const fee = this._authTxnResult()?.fee.creditor ?? this._selectedMethod()?.fee ?? 0;
    return base + fee;
  });

  /** Fee amount for display */
  readonly feeAmount = computed(
    () => this._authTxnResult()?.fee.creditor ?? this._selectedMethod()?.fee ?? 0,
  );

  /**
   * Enable "Continuer vers confirmation" when:
   *  - valid amount
   *  - method selected
   *  - for PSP methods: a token is selected
   *  - auth_txn resolved (not loading + result present)
   */
  readonly canContinueToConfirm = computed(() => {
    const method = this._selectedMethod();
    const tokenOk =
      method?.is_gozem_money_processor || !method?.processor || this._selectedToken() !== null;
    return (
      this.isAmountValid() &&
      method !== null &&
      tokenOk &&
      !this._authTxnLoading() &&
      this._authTxnResult() !== null
    );
  });

  // ── Subjects (cancellable pipelines) ──────────────────────────────────────

  private readonly methodsTrigger$ = new Subject<number>();
  private readonly tokensTrigger$ = new Subject<string>(); // processorId
  private readonly recipientsTrigger$ = new Subject<void>();
  private readonly addTokenTrigger$ = new Subject<AddTokenFormValue>();

  private readonly authTxnTrigger$ = new Subject<{
    amount: number;
    processorId: string;
    methodId: string;
    beneficiaryPhone?: string; // PSP: extracted from selected token's properties
    accountNumber?: string; // Gozem Money: entered by user in add-account form
  }>();

  private readonly submitTrigger$ = new Subject<{
    amount: number;
    isGozemPath: boolean;
    // Gozem path
    payeeGmAccountNumber?: string;
    beneficiaryFullName?: string;
    beneficiaryPhoneNumber?: string;
    // PSP path
    processorId?: string;
    tokenId?: string;
    withdrawalId?: string;
    // common
    categoryId: string;
    fees: { fee: TopupFee; commission: TopupFee };
    message: string;
    authenticationData?: string;
  }>();

  private readonly _detailTrigger$ = new Subject<string>();

  constructor() {
    this.wireMethodsPipeline();
    this.wireTokensPipeline();
    this.wireRecipientsPipeline();
    this.wireAuthTxnPipeline();
    this.wireAddTokenPipeline();
    this.wireSubmitPipeline();
    this.wireDetailPipeline();
  }

  // ── Actions ───────────────────────────────────────────────────────────────

  /**
   * Load transfer methods for the given amount.
   * Called when the user advances from the amount step to the method step.
   */
  loadMethods(amount: number): void {
    this._amount.set(amount);
    this._methodsLoading.set(true);
    this._methodsError.set(null);
    this._methods.set([]);
    this._selectedMethod.set(null);
    this._authTxnResult.set(null);
    this._authTxnError.set(null);
    this._categoryId.set('');
    this._error.set(null);
    this.methodsTrigger$.next(amount);
  }

  /**
   * Select a transfer method.
   * For PSP methods (with processor): triggers token fetch before auth_txn.
   * For Gozem Money methods: fires auth_txn immediately.
   */
  selectMethod(method: TopupMethod): void {
    if (this._selectedMethod()?.id === method.id) return;
    this._selectedMethod.set(method);
    this._authTxnResult.set(null);
    this._authTxnError.set(null);
    this._categoryId.set('');
    this._error.set(null);
    // Reset token state on every method change
    this._tokens.set([]);
    this._selectedToken.set(null);
    this._tokensError.set(null);

    if (method.processor?._id) {
      this._tokensLoading.set(true);
      if (method.is_gozem_money_processor) {
        // Gozem Money: fetch recent recipients instead of PSP tokens
        this.recipientsTrigger$.next();
      } else {
        // PSP method: fetch saved tokens — auth_txn fires after selectToken()
        this.tokensTrigger$.next(method.processor._id);
      }
    } else {
      // No processor: fire auth_txn immediately
      this.triggerAuthTxn(method);
    }
  }

  /**
   * Re-run the auth_txn for the currently selected method.
   * Unlike selectMethod(), this bypasses the same-method-ID guard, so it
   * always fires even when the selection has not changed — needed for the
   * "retry" path after an auth_txn error.
   */
  retryAuthTxn(): void {
    const method = this._selectedMethod();
    if (!method) return;
    this._authTxnError.set(null);
    this._authTxnResult.set(null);
    this.triggerAuthTxn(method);
  }

  /** Select a saved token (PSP path). Always fires auth_txn immediately. */
  selectToken(token: TopupToken): void {
    this._selectedToken.set(token);
    this._authTxnResult.set(null);
    this._authTxnError.set(null);
    const method = this._selectedMethod();
    if (method) this.triggerAuthTxn(method);
  }

  /** Re-fetch tokens (PSP) or recipients (Gozem Money) for the selected method. */
  retryTokens(): void {
    const method = this._selectedMethod();
    if (!method?.processor?._id) return;
    this._tokensError.set(null);
    this._tokensLoading.set(true);
    if (method.is_gozem_money_processor) {
      this.recipientsTrigger$.next();
    } else {
      this.tokensTrigger$.next(method.processor._id);
    }
  }

  /** Add a new token via the dynamic form. Auto-selects on success. */
  addToken(form: AddTokenFormValue): void {
    this._addTokenLoading.set(true);
    this._addTokenError.set(null);
    this.addTokenTrigger$.next(form);
  }

  setCategoryId(id: string): void {
    this._categoryId.set(id);
  }

  setMessage(msg: string): void {
    this._message.set(msg);
  }

  /** Clear the submit error — used when the user navigates back to retry. */
  clearError(): void {
    this._error.set(null);
  }

  /** Reset auth_txn state — called when entering the Gozem Money add-account form. */
  clearAuthTxn(): void {
    this._authTxnResult.set(null);
    this._authTxnError.set(null);
  }

  /**
   * Gozem Money add-account: verify the account_number via auth_txn.
   * Skips token creation entirely. On success the authTxnResult signal is
   * populated and the component effect advances directly to the confirm step.
   */
  verifyGozemAccount(accountNumber: string): void {
    const method = this._selectedMethod();
    const amount = this._amount();
    if (!method || amount === null) return;
    const recipient = this._tokens().find((t) => t._id === accountNumber);
    if (recipient) this._selectedToken.set(recipient);
    this._authTxnResult.set(null);
    this._authTxnError.set(null);
    this._authTxnLoading.set(true);
    this.authTxnTrigger$.next({
      amount,
      processorId: method.processor?._id ?? '',
      methodId: method.id,
      accountNumber,
    });
  }

  /**
   * Called when the user confirms with their PIN on the confirm step.
   * RSA-encrypts the PIN then dispatches the correct submit path.
   */
  async onPinEntered(pin: string): Promise<void> {
    const method = this._selectedMethod();
    const amount = this._amount();
    const authTxn = this._authTxnResult();

    if (!method || amount === null || !authTxn) return;
    // Backend owns lockout: never dispatch another attempt once locked.
    if (this.pinStore.pinLocked()) return;

    this._error.set(null);
    this.pinStore.clearError();
    this._submitting.set(true);

    const isGozemPath = method.is_gozem_money_processor;

    let authenticationData: string | undefined;
    if (authTxn.is_pin_required) {
      try {
        authenticationData = await this.pinEncryption.encryptPin(pin);
      } catch {
        this._error.set('Erreur de chiffrement du PIN. Veuillez réessayer.');
        this._submitting.set(false);
        return;
      }
    }

    if (isGozemPath) {
      // Guard: payee_gm_account_number must be present for internal Gozem transfers.
      // The backend returns it in the auth_txn response for Gozem Money methods.
      const payeeGmAccountNumber = authTxn.payee_gm_account_number;
      if (!payeeGmAccountNumber) {
        this._submitting.set(false);
        this.emitFailure(
          'Le compte Gozem Money du bénéficiaire est introuvable. Veuillez réessayer.',
        );
        return;
      }

      this.submitTrigger$.next({
        amount,
        isGozemPath: true,
        payeeGmAccountNumber,
        beneficiaryFullName: authTxn.beneficiary_full_name,
        beneficiaryPhoneNumber: authTxn.beneficiary_phone_number,
        categoryId: this._categoryId(),
        fees: { fee: authTxn.fee, commission: authTxn.commission },
        message: this._message(),
        authenticationData,
      });
    } else {
      this.submitTrigger$.next({
        amount,
        isGozemPath: false,
        processorId: method.processor?._id ?? '',
        tokenId: this._selectedToken()?._id ?? '',
        withdrawalId: authTxn.withdrawal_id,
        categoryId: this._categoryId(),
        fees: { fee: authTxn.fee, commission: authTxn.commission },
        message: this._message(),
        authenticationData,
      });
    }
  }

  /** Reset all state — call on dialog close or new send */
  reset(): void {
    this._amount.set(null);
    this._dialogStep.set('form');
    this._methods.set([]);
    this._methodsLoading.set(false);
    this._methodsError.set(null);
    this._selectedMethod.set(null);
    this._tokens.set([]);
    this._tokensLoading.set(false);
    this._tokensError.set(null);
    this._selectedToken.set(null);
    this._addTokenLoading.set(false);
    this._addTokenError.set(null);
    this._authTxnResult.set(null);
    this._authTxnLoading.set(false);
    this._authTxnError.set(null);
    this._categoryId.set('');
    this._message.set('');
    this._submitting.set(false);
    this._error.set(null);
    this._submitFailure.set(null);
    this._lastSendResult.set(null);
    this.pinStore.reset();
  }

  // ── Private helpers ───────────────────────────────────────────────────────

  /**
   * Distinguish an authentication-stage rejection (wrong PIN, lockout, or a
   * malformed-PIN rejection) from a genuine transaction failure. Delegated to
   * the shared {@link isAuthStageError} classifier so send + withdrawal stay in
   * lockstep. Auth-stage errors keep the user on the PIN step; everything else
   * routes to the terminal failure screen.
   */
  private isPinError(err: unknown): boolean {
    return isAuthStageError(err);
  }

  /**
   * Surface a terminal transaction failure. Navigation is driven off a monotonic
   * `seq` so that consecutive identical failures always notify subscribers —
   * setting `_dialogStep` alone is a no-op when it is already 'failure', which
   * previously left the user stranded with no feedback on repeat attempts.
   */
  private emitFailure(message: string): void {
    this._error.set(message);
    this._dialogStep.set('failure');
    this._submitFailure.set({ message, seq: ++this.submitFailureSeq });
  }

  private triggerAuthTxn(method: TopupMethod): void {
    const amount = this._amount();
    if (amount === null) return;

    this._authTxnLoading.set(true);
    this._authTxnResult.set(null);

    const token = this._selectedToken();

    // PSP: the beneficiary phone comes from the token's `int_phone` property.
    // Gozem Money: no beneficiary identifier — auth_txn fires without one.
    //
    // `int_phone` is optional on a token, and the previous `` `+${phone}` `` produced
    // the bare string '+' when it was missing. '+' is truthy, so the API layer's
    // omit-guard forwarded it and the backend answered "1032 - Gozem money user not
    // found". toE164 returns undefined instead, so the field is omitted entirely.
    //
    // Deliberately NOT falling back to `display_key` despite the older comment here
    // claiming so: display_key is a human label, not an identifier — for card tokens
    // it holds masked text like 'Card •••• 4242', which would yield the bogus phone
    // '+4242' and simply move the failed lookup somewhere harder to diagnose.
    let beneficiaryPhone: string | undefined;
    if (method.processor && token) {
      beneficiaryPhone = toE164(token.token_properties['int_phone']);
    }

    this.authTxnTrigger$.next({
      amount,
      processorId: method.processor?._id ?? '',
      methodId: method.id,
      beneficiaryPhone,
    });
  }

  // ── Pipelines ─────────────────────────────────────────────────────────────

  private wireTokensPipeline(): void {
    this.flow.wire(this.tokensTrigger$, (processorId) => this.sendService.getTokens(processorId), {
      next: (tokens) => {
        this._tokens.set(tokens);
        this._tokensLoading.set(false);
      },
      error: (err) => {
        this._tokensError.set((err as Error).message);
        this._tokensLoading.set(false);
      },
    });
  }

  private wireRecipientsPipeline(): void {
    this.recipientsTrigger$
      .pipe(
        switchMap(() =>
          this.sendService.getRecipients().pipe(
            catchError((err: Error) => {
              this._tokensError.set(err.message);
              this._tokensLoading.set(false);
              return EMPTY;
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((tokens) => {
        this._tokens.set(tokens);
        this._tokensLoading.set(false);
      });
  }

  private wireAddTokenPipeline(): void {
    this.flow.wire(
      this.addTokenTrigger$,
      (form) => {
        const method = this._selectedMethod();
        // Silent abort (no error, loading unchanged) when the method has no
        // processor — preserved from the original behavior.
        if (!method?.processor) return EMPTY;
        return this.addTokenService.createToken(form, method, 2);
      },
      {
        next: (token) => {
          this._tokens.update((prev) => [token, ...prev]);
          this._addTokenLoading.set(false);
          this.selectToken(token);
        },
        error: (err) => {
          this._addTokenError.set((err as Error).message);
          this._addTokenLoading.set(false);
        },
      },
    );
  }

  private wireMethodsPipeline(): void {
    this.flow.wire(this.methodsTrigger$, (amount) => this.sendService.getMethods(amount), {
      next: (methods) => {
        this._methods.set(methods);
        this._methodsLoading.set(false);
      },
      error: (err) => {
        this._methodsError.set((err as Error).message);
        this._methodsLoading.set(false);
      },
    });
  }

  private wireAuthTxnPipeline(): void {
    this.authTxnTrigger$
      .pipe(
        // Debounce so rapid phone keystrokes don't flood the network.
        // switchMap already cancels in-flight requests, but the debounce
        // prevents even dispatching unnecessary requests.
        debounceTime(300),
        // defer: a synchronous throw from the service/API layer would otherwise escape this
        // projection, bypass the catchError below and kill the subscription permanently.
        switchMap(({ amount, processorId, methodId, beneficiaryPhone, accountNumber }) =>
          defer(() =>
            this.sendService.authTxn(
              amount,
              processorId,
              methodId,
              beneficiaryPhone,
              accountNumber,
            ),
          ).pipe(
            catchError((err: Error) => {
              this._authTxnError.set(err.message);
              this._authTxnLoading.set(false);
              return EMPTY;
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        this._authTxnResult.set(result);
        this._authTxnLoading.set(false);
      });
  }

  private wireSubmitPipeline(): void {
    this.submitTrigger$
      .pipe(
        switchMap((payload) => {
          // defer: the service call runs while building this observable, so a synchronous
          // throw must stay inside the stream to reach the catchError below. Escaping the
          // projection would kill the subscription and strand the dialog mid-transfer.
          const transfer$ = defer(() =>
            payload.isGozemPath
              ? this.sendService.initTransfer({
                  amount: payload.amount,
                  payeeGmAccountNumber: payload.payeeGmAccountNumber!,
                  categoryId: payload.categoryId,
                  fees: payload.fees,
                  message: payload.message,
                  authenticationData: payload.authenticationData,
                  beneficiaryFullName: payload.beneficiaryFullName,
                  beneficiaryPhoneNumber: payload.beneficiaryPhoneNumber,
                })
              : this.sendService.initPspTransfer({
                  amount: payload.amount,
                  processorId: payload.processorId!,
                  tokenId: payload.tokenId!,
                  withdrawalId: payload.withdrawalId!,
                  categoryId: payload.categoryId,
                  fees: payload.fees,
                  message: payload.message,
                  authenticationData: payload.authenticationData,
                }),
          );

          return transfer$.pipe(
            catchError((err: unknown) => {
              this._submitting.set(false);

              // ── Authentication error (wrong PIN / lockout) ────────────
              // A PIN error is one the backend tags with remaining_attempts.
              // This is NOT a transaction failure — the transfer was never
              // attempted — so keep the user on the PIN step and let
              // PinAttemptStore own the message + lockout (remaining === 0).
              if (this.isPinError(err)) {
                this.pinStore.recordFailure(err);
                return EMPTY;
              }

              // ── Transaction failure ───────────────────────────────────
              // PIN was accepted but the transfer itself failed → terminal
              // "Transfert échoué" result screen with the server's message.
              this.emitFailure(
                err instanceof GozemApiError
                  ? err.displayMessage
                  : "Le transfert n'a pas pu être traité. Veuillez réessayer.",
              );
              return EMPTY;
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        this._submitting.set(false);
        // PIN accepted — clear any accumulated attempt/lockout state.
        this.pinStore.reset();
        const method = this._selectedMethod();
        const fee = this._authTxnResult()?.fee.creditor ?? 0;

        this._lastSendResult.set({
          transactionId: result.transaction_id,
          referenceId: result.transaction_unique_id,
          paymentId: result.payment_id,
          amount: this._amount() ?? 0,
          beneficiaryName: this._selectedMethod()?.is_gozem_money_processor
            ? (this._authTxnResult()?.beneficiary_full_name ?? '')
            : (this._selectedToken()?.token_name ?? ''),
          methodName: method?.name ?? '',
          fee,
          wallet: result.wallet ?? undefined,
        });

        this._dialogStep.set('success');
        this._txDetailLoading.set(true);
        this._txStatus.set(null);
        this._detailTrigger$.next(result.payment_id);
      });
  }

  private wireDetailPipeline(): void {
    this._detailTrigger$
      .pipe(
        switchMap((paymentId) =>
          this.transactionStatus.loadStatus(paymentId).pipe(
            // Reset the loading flag when the request settles — on success, on
            // an empty result, and (the fix) on error. `loadStatus` swallows
            // errors into EMPTY, so the stream completes without emitting; the
            // old code cleared the flag only in the `next` handler, leaving
            // txDetailLoading stuck `true` after a failed status fetch.
            finalize(() => this._txDetailLoading.set(false)),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((status) => {
        this._txStatus.set(status);
      });
  }
}
